import React from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Section, Badge, Banner } from '../ui';
import { PushNotificationToggle } from '../PushNotificationToggle';
import { cn } from '../../utils/cn';

export interface ChannelsGroupProps {
  /** Tighter spacing/padding for the Telegram Mini-App layout (T7b). */
  density?: 'default' | 'compact';
  currentChannel: string;
  onChange: (channel: string) => void;
  isLoading: boolean;
  isPremium: boolean;
  hasEmail: boolean;
  isEmailVerified: boolean;
  pushPermissionDenied: boolean;
  showPushWarning: boolean;
}

interface ChannelOption {
  value: string;
  label: string;
  description: string;
  disabled?: boolean;
  disabledNote?: string;
  gated?: boolean;
}

/** "Meldung per" group: single-choice channel list plus the device push switch. */
export const ChannelsGroup: React.FC<ChannelsGroupProps> = ({
  density = 'default',
  currentChannel,
  onChange,
  isLoading,
  isPremium,
  hasEmail,
  isEmailVerified,
  pushPermissionDenied,
  showPushWarning,
}) => {
  const isCompact = density === 'compact';
  const bannerSpacing = isCompact ? 'mt-2' : 'mt-3';
  const options: ChannelOption[] = [
    { value: 'telegram', label: 'Telegram', description: 'Meldungen direkt im Chat.' },
    {
      value: 'push',
      label: 'Push',
      description: 'Meldungen im Browser, auch wenn die App geschlossen ist.',
      disabled: pushPermissionDenied,
      disabledNote: pushPermissionDenied ? 'Berechtigung verweigert' : undefined,
    },
    {
      value: 'email',
      label: 'E-Mail',
      description: 'Meldungen als E-Mail.',
      disabled: !isPremium || !isEmailVerified,
      gated: !isPremium,
      disabledNote: isPremium && !isEmailVerified ? 'Bestätige zuerst deine E-Mail-Adresse.' : undefined,
    },
    { value: 'none', label: 'Keine Meldungen', description: 'Du bekommst keine Benachrichtigungen für neue Treffer.' },
  ];

  return (
    <Section title="Meldung per" icon={<Bell className="h-5 w-5" aria-hidden="true" />}>
      <div role="radiogroup" aria-label="Benachrichtigungsart" className={cn('flex flex-col', isCompact ? 'gap-1.5' : 'gap-2')}>
        {options.map((opt) => {
          const selected = currentChannel === opt.value;
          return (
            <label
              key={opt.value}
              className={cn(
                'flex items-start gap-3 rounded-control border transition-colors',
                isCompact ? 'p-2' : 'p-3',
                selected ? 'border-brand bg-brand-soft' : 'border-line-strong hover:bg-sunken',
                opt.disabled && 'cursor-not-allowed opacity-60'
              )}
            >
              <input
                type="radio"
                name="notificationChannel"
                value={opt.value}
                checked={selected}
                disabled={isLoading || opt.disabled}
                onChange={() => onChange(opt.value)}
                className="mt-1 h-4 w-4 accent-brand"
              />
              <span className="flex flex-col">
                <span className="flex items-center gap-2 text-sm font-medium text-ink">
                  {opt.label}
                  {opt.gated && <Badge tone="brand">ab Pro</Badge>}
                </span>
                <span className="text-sm text-muted">
                  {opt.description}
                  {opt.disabledNote ? ` (${opt.disabledNote})` : ''}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {currentChannel === 'email' && !isPremium && (
        <Banner tone="warning" className={bannerSpacing}>E-Mail-Benachrichtigungen sind ab dem Pro-Plan verfügbar.</Banner>
      )}

      {isPremium && hasEmail && !isEmailVerified && (
        <Banner tone="warning" className={bannerSpacing}>
          Deine E-Mail-Adresse ist noch nicht verifiziert.{' '}
          <Link to="/dashboard" className="underline underline-offset-4">Verifikationslink erneut senden</Link>
        </Banner>
      )}

      {isPremium && !hasEmail && (
        <Banner tone="warning" className={bannerSpacing}>
          Keine E-Mail-Adresse hinterlegt.{' '}
          <Link to="/dashboard/settings" className="underline underline-offset-4">In den Einstellungen hinzufügen</Link>
        </Banner>
      )}

      {showPushWarning && (
        <Banner tone="info" title="Push-Benachrichtigungen aktivieren" className={bannerSpacing}>
          <p className="mb-3">Um Benachrichtigungen direkt im Browser zu erhalten, muss dieses Gerät registriert sein.</p>
          <PushNotificationToggle />
        </Banner>
      )}
    </Section>
  );
};
